// src/hooks/useGlobalEntityCacheInvalidation.js
import { useEffect, useEffectEvent, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ensureConnected, joinGroups, leaveGroups, onChange } from '../realtime/realtimeClient';
import { useTenantSettings } from './useTenantSettings';
import { createLoadDetailRefreshScheduler } from '../pages/loads/utils/loadDetailSignalRRefresh';
import { removeEntityLookupQueries } from '../utils/entityLookupCache';
import {
    applyListRowDeltaToQueryClient,
    collectLoadedIds,
    toCrudOp,
} from '../utils/entityListRowCache';
import { extraListKeysForEntity, getEntityListRowFetcher } from '../utils/entityListRowFetchers';

const LIST_DELTA_DEBOUNCE_MS = 250;

/**
 * Global hook to keep React Query caches fresh when any entity is updated via SignalR.
 *
 * **ARCHITECTURE:**
 * - This is the ONLY SignalR listener for entity changes in the application
 * - Page-specific SignalR listeners have been removed to prevent group management conflicts
 * - This hook joins ALL entity groups and owns the SignalR connection lifecycle
 *
 * **INVALIDATION STRATEGY:**
 * 1. List queries: patch the loaded row (byIds / getLoadDetail). Insert the row
 *    when it now belongs in the loaded sort window (e.g. description changed to "A").
 *    Full list refetch only when the event has no entity id, or the row is loaded
 *    and cannot be fetched singly.
 * 2. Lookup queries: Removed from cache (not merely marked stale) so weighing /
 *    booking / LookupModal never reuse old options after a SignalR event
 * 3. Detail queries (singular): Only invalidated if entity ID matches
 *
 * Should be placed at the app root level (e.g., AppLayout).
 */
export function useGlobalEntityCacheInvalidation() {
    const queryClient = useQueryClient();
    const listDeltaQueueRef = useRef(new Map());
    const listDeltaTimerRef = useRef(null);
    const loadDetailRefreshRef = useRef(null);
    if (loadDetailRefreshRef.current == null) {
        loadDetailRefreshRef.current = createLoadDetailRefreshScheduler();
    }
    const { data: tenantSettings } = useTenantSettings();
    const loggingEnabled = tenantSettings?.enableCacheInvalidationLogging ?? false;

    const log = useEffectEvent((...args) => {
        if (loggingEnabled) {
            console.log(...args);
        }
    });

    const flushListDeltas = useEffectEvent(async (isCancelled) => {
        listDeltaTimerRef.current = null;
        const qc = queryClient;
        const batch = Array.from(listDeltaQueueRef.current.values());
        listDeltaQueueRef.current.clear();
        if (!batch.length) return;

        const byListKey = new Map();
        for (const item of batch) {
            if (!byListKey.has(item.listKey)) byListKey.set(item.listKey, []);
            byListKey.get(item.listKey).push(item);
        }

        for (const [listKey, items] of byListKey) {
            if (isCancelled?.()) return;

            const loaded = collectLoadedIds(qc, listKey);
            const deleteIds = [];
            const upsertIds = [];

            for (const { id, op } of items) {
                if (op === 'D') {
                    if (loaded.has(id)) deleteIds.push(id);
                    continue;
                }
                // Fetch I/U even when the id is not in the loaded pages so a row that
                // now sorts into the current window (e.g. description -> "A") can be inserted.
                upsertIds.push(id);
            }

            if (deleteIds.length) {
                applyListRowDeltaToQueryClient(qc, listKey, { deleteIds });
                log('[CacheInvalidation] Removed list rows for', listKey, deleteIds);
            }

            if (!upsertIds.length) continue;

            const fetcher = getEntityListRowFetcher(listKey);
            if (!fetcher) {
                const loadedUpserts = upsertIds.filter((id) => loaded.has(id));
                if (loadedUpserts.length) {
                    log('[CacheInvalidation] No byIds fetcher for', listKey, '- invalidating loaded list');
                    qc.invalidateQueries({ queryKey: [listKey], exact: false });
                }
                continue;
            }

            try {
                const refreshedRows = await fetcher(upsertIds);
                if (isCancelled?.()) return;
                applyListRowDeltaToQueryClient(qc, listKey, {
                    askedIds: upsertIds,
                    refreshedRows,
                });
                log('[CacheInvalidation] Patched list rows for', listKey, upsertIds);
            } catch (err) {
                if (loggingEnabled) console.warn('[CacheInvalidation] byIds failed for', listKey, err);
                qc.invalidateQueries({ queryKey: [listKey], exact: false });
            }
        }
    });

    const enqueueListDelta = useEffectEvent((listKey, id, op, isCancelled) => {
        const key = `${listKey}:${id}`;
        const prev = listDeltaQueueRef.current.get(key);
        if (prev?.op === 'D') return;
        listDeltaQueueRef.current.set(key, { listKey, id, op });

        if (listDeltaTimerRef.current) clearTimeout(listDeltaTimerRef.current);
        listDeltaTimerRef.current = setTimeout(() => {
            flushListDeltas(isCancelled);
        }, LIST_DELTA_DEBOUNCE_MS);
    });

    const handleChange = useEffectEvent((evt, isCancelled) => {
        const qc = queryClient;

        log('[CacheInvalidation] GLOBAL handler received event:', evt, 'at', new Date().toISOString());
        if (isCancelled()) {
            log('[CacheInvalidation] Handler cancelled, ignoring event');
            return;
        }

        const entityName = evt?.entityName ?? evt?.EntityName;
        const raw = evt?.raw ?? evt;
        const entityId =
            evt?.entityId ??
            evt?.EntityId ??
            raw?.entityId ??
            raw?.EntityId ??
            raw?.loadDetailId ??
            raw?.LoadDetailId ??
            raw?.id ??
            raw?.Id ??
            raw?.loadDetail?.id ??
            raw?.loadDetail?.Id ??
            raw?.LoadDetail?.id ??
            raw?.LoadDetail?.Id ??
            raw?.entity?.id ??
            raw?.entity?.Id ??
            raw?.Entity?.id ??
            raw?.Entity?.Id ??
            null;

        log('[CacheInvalidation] Processing event for entity:', entityName, 'id:', entityId);

        if (!entityName) {
            log('[CacheInvalidation] No entityName in event, skipping');
            return;
        }

        // Normalize entity name to lowercase for query key matching
        const normalizedName = entityName.toLowerCase();
        const singularName = getSingularName(normalizedName);
        // Use mapped list key so e.g. IncompleteLoads -> incompleteLoads (grid query key)
        const listQueryKey = LIST_QUERY_KEY_BY_ENTITY[entityName] ?? LIST_QUERY_KEY_BY_ENTITY[normalizedName] ?? normalizedName;
        const detailQueryKey = DETAIL_QUERY_KEY_BY_ENTITY[entityName] ?? DETAIL_QUERY_KEY_BY_ENTITY[normalizedName];
        const crudOp = toCrudOp(evt) ?? 'U';
        const id = entityId != null ? Number(entityId) : NaN;
        const hasRowId = Number.isFinite(id);

        log('[CacheInvalidation] Updating caches for:', normalizedName, 'singular:', singularName, 'listKey:', listQueryKey, 'detailKey:', detailQueryKey, 'op:', crudOp);

        const isLoadDetailsEntity =
            entityName === 'LoadDetails' ||
            entityName === 'LOAD_DETAILS' ||
            entityName === 'Loads' ||
            entityName === 'LOADS' ||
            normalizedName === 'loaddetails' ||
            normalizedName === 'load_details' ||
            normalizedName === 'loads';

        // List grids: patch the loaded row. Full refetch only when we cannot target a row.
        if (!hasRowId) {
            qc.invalidateQueries({ queryKey: [listQueryKey], exact: false });
            if (isLoadDetailsEntity) {
                qc.invalidateQueries({ queryKey: ['completedLoads'], exact: false });
                qc.invalidateQueries({ queryKey: ['deletedLoads'], exact: false });
            }
            if (entityName === 'Locations' || normalizedName === 'locations') {
                qc.invalidateQueries({ queryKey: ['depots'], exact: false });
            }
        } else if (!isLoadDetailsEntity) {
            enqueueListDelta(listQueryKey, id, crudOp, isCancelled);
            for (const extraKey of extraListKeysForEntity(entityName, normalizedName)) {
                enqueueListDelta(extraKey, id, crudOp, isCancelled);
            }
        }

        // Nested hatches on the ship form are not an infinite entity grid.
        if (entityName === 'Locations' || normalizedName === 'locations') {
            qc.invalidateQueries({ queryKey: ['shipHatches'], exact: false });
            qc.invalidateQueries({ queryKey: ['depot'], exact: false });
        }

        // Drop lookup caches entirely (weighing, booking, LookupModal aliases)
        removeEntityLookupQueries(qc, entityName);
        log('[CacheInvalidation] Removed lookup caches for entity:', entityName);

        // Targeted invalidation: if we have an entity ID, invalidate only queries matching that specific ID
        if (hasRowId) {
            const idStr = String(entityId);
            if (detailQueryKey === 'loadDetail') {
                loadDetailRefreshRef.current.schedule(qc, id, crudOp, isCancelled);
                log('[CacheInvalidation] Scheduled coalesced loadDetail refresh for id:', id);
            }

            log('[CacheInvalidation] Targeted invalidation for entity:', entityName, 'id:', id);

            // Helper function to check if a query matches this entity ID (list or detail)
            const matchesEntityId = (query) => {
                const key = query.queryKey;
                if (!Array.isArray(key) || key.length < 2) return false;
                const firstPart = key[0];
                const hasId = key.some((part) => part === id || part === idStr);
                // Standard detail key (e.g. account, contract)
                if (firstPart === singularName || firstPart === normalizedName) return hasId;
                // Scheduler owns ['loadDetail', id] refetch so a tare/trigger audit burst
                // does not fire four overlapping getLoadDetail calls.
                if (detailQueryKey === 'loadDetail' && firstPart === 'loadDetail') return false;
                if (detailQueryKey && firstPart === detailQueryKey && (key[1] === id || key[1] === idStr)) return true;
                // Completed loads detail page uses queryKey ['completedLoadDetail', loadId]
                if (firstPart === 'completedLoadDetail' && (key[1] === id || key[1] === idStr)) return true;
                return false;
            };

            // Invalidate active queries that match this specific entity ID
            qc.invalidateQueries({
                predicate: matchesEntityId,
            });
        }
    });

    /* eslint-disable react-hooks/exhaustive-deps -- handleChange/log/enqueue/flush are useEffectEvents */
    useEffect(() => {
        log('[CacheInvalidation] Hook EFFECT RUNNING - mounting/updating');
        let off = () => { };
        let cancelled = false;
        const isCancelled = () => cancelled;

        // List of all entity names that have SignalR groups
        // These should match the entity names emitted by MultiTenantAuditBroadcaster (TableToEntity)
        // and the names used in useLiveEntityRefresh throughout the app.
        const allEntityNames = [
            'Accounts',
            'Commodities',
            'Contracts',
            'Drivers',
            'Hauliers',
            'Locations',
            'Ships',
            'LaAreas',
            'Trailers',
            'Vehicles',
            'Users',
            'IncompleteLoads',
            'CompletedLoads',
            // Backend broadcaster maps LOAD_DETAILS -> "LoadDetails" (pascal-cased),
            // so we must join that exact group name to receive load-detail events.
            'LoadDetails',
            // Keep legacy group as well in case any other publishers still use it
            'LOAD_DETAILS',
        ];

        // Build groups array: each entity name + uppercase version (matching useLiveEntityRefresh pattern)
        const allGroups = allEntityNames.flatMap((name) => [name, name.toUpperCase()]);

        (async () => {
            log('[CacheInvalidation] Hook mounting, connecting to SignalR...');
            const conn = await ensureConnected();
            log('[CacheInvalidation] Connection state:', conn.state);
            if (cancelled) return;

            log('[CacheInvalidation] Joining groups:', allGroups);
            // Join all entity groups to receive updates for any entity
            await joinGroups(allGroups);
            log('[CacheInvalidation] Successfully joined groups, connection state:', conn.state);
            if (cancelled) return;
            log('[CacheInvalidation] Setting up onChange handler');

            const handlerId = `global-cache-invalidation-${Date.now()}`;
            log('[CacheInvalidation] Registering GLOBAL onChange handler with ID:', handlerId);
            off = onChange((evt) => {
                handleChange(evt, isCancelled);
            });
            log('[CacheInvalidation] onChange handler registered successfully');
        })();

        return () => {
            log('[CacheInvalidation] Hook EFFECT CLEANUP - unmounting/updating, cleaning up...');
            cancelled = true;
            if (listDeltaTimerRef.current) {
                clearTimeout(listDeltaTimerRef.current);
                listDeltaTimerRef.current = null;
            }
            listDeltaQueueRef.current.clear();
            loadDetailRefreshRef.current?.dispose();
            loadDetailRefreshRef.current = null;
            const cleanupResult = off();
            log('[CacheInvalidation] Cleanup function called, result:', cleanupResult);
            leaveGroups(allGroups).catch((err) => {
                log('[CacheInvalidation] Error leaving groups:', err);
            });
        };
    }, []);
    /* eslint-enable react-hooks/exhaustive-deps */
}

/**
 * Convert plural entity name to singular.
 * Handles common cases: contracts -> contract, accounts -> account, etc.
 */
function getSingularName(pluralName) {
    // Common pluralization rules
    if (pluralName.endsWith('ies')) {
        return pluralName.slice(0, -3) + 'y'; // e.g., "commodities" -> "commodity"
    }
    if (pluralName.endsWith('es')) {
        return pluralName.slice(0, -2); // e.g., "vehicles" -> "vehicle"
    }
    if (pluralName.endsWith('s')) {
        return pluralName.slice(0, -1); // e.g., "contracts" -> "contract"
    }
    return pluralName; // Already singular or unknown pattern
}

/** List query key first segment by SignalR entity name (when it differs from lowercase). */
const LIST_QUERY_KEY_BY_ENTITY = {
    IncompleteLoads: 'incompleteLoads',
    incompleteloads: 'incompleteLoads',
    CompletedLoads: 'completedLoads',
    completedloads: 'completedLoads',
    LoadDetails: 'incompleteLoads',
    loaddetails: 'incompleteLoads',
    LOAD_DETAILS: 'incompleteLoads',
    load_details: 'incompleteLoads',
    LaAreas: 'laAreas',
    laareas: 'laAreas',
};

/** Detail query key first segment by SignalR entity name (for load detail panel). */
const DETAIL_QUERY_KEY_BY_ENTITY = {
    IncompleteLoads: 'loadDetail',
    incompleteloads: 'loadDetail',
    LoadDetail: 'loadDetail',
    loaddetail: 'loadDetail',
    LoadDetails: 'loadDetail',
    loaddetails: 'loadDetail',
    Load: 'loadDetail',
    load: 'loadDetail',
    Loads: 'loadDetail',
    loads: 'loadDetail',
    LOADS: 'loadDetail',
    CompletedLoads: 'loadDetail',
    completedloads: 'loadDetail',
    LOAD_DETAILS: 'loadDetail',
    load_details: 'loadDetail',
};
